import React from 'react';
import ONBOARDING_LOCALES from '../i18n/onboardingLocales.json';
import { normalizeUFOVLanguage } from '../i18n/ufovLocales';

const parseOnboardingProgress = (value) => {
    if (value && typeof value === 'object') {
        const current = Number(value.current);
        const total = Number(value.total);

        if (Number.isFinite(current) && Number.isFinite(total) && total > 0) {
            return {
                current: Math.max(1, Math.min(total, current)),
                total,
            };
        }
    }

    const match = `${value || ''}`.match(/(\d+)\s*\/\s*(\d+)/);
    if (!match) {
        return null;
    }

    const current = Number(match[1]);
    const total = Number(match[2]);

    if (!Number.isFinite(current) || !Number.isFinite(total) || total <= 0) {
        return null;
    }

    return {
        current: Math.max(1, Math.min(total, current)),
        total,
    };
};

const formatProgressLabel = (template, values) => Object.entries(values).reduce(
    (label, [key, value]) => label.replaceAll(`{${key}}`, `${value}`),
    `${template || ''}`
);

const getProgressLabelTemplate = (language = 'en') => {
    const normalizedLanguage = normalizeUFOVLanguage(language);

    return ONBOARDING_LOCALES[normalizedLanguage]?.taste?.stepProgress
        || ONBOARDING_LOCALES.en.taste.stepProgress;
};

function OnboardingProgress({
    ariaLabel = '',
    className = '',
    current = null,
    labelTemplate = '',
    language = 'en',
    step = '',
    total = null,
}) {
    const normalizedLanguage = normalizeUFOVLanguage(language);
    const progress = parseOnboardingProgress(
        current !== null
            && current !== undefined
            && total !== null
            && total !== undefined
            && Number.isFinite(Number(current))
            && Number.isFinite(Number(total))
            ? { current, total }
            : step
    );

    if (!progress) {
        return null;
    }

    let numberFormatter;
    try {
        numberFormatter = new Intl.NumberFormat(normalizedLanguage);
    } catch (error) {
        numberFormatter = new Intl.NumberFormat('en');
    }

    const localizedValues = {
        current: numberFormatter.format(progress.current),
        total: numberFormatter.format(progress.total),
    };
    const resolvedLabelTemplate = labelTemplate || getProgressLabelTemplate(normalizedLanguage);
    const accessibleLabel = ariaLabel
        || formatProgressLabel(resolvedLabelTemplate, localizedValues)
        || `${localizedValues.current}/${localizedValues.total}`;
    const percent = Math.round((progress.current / progress.total) * 100);

    return (
        <div
            aria-label={accessibleLabel}
            aria-valuemax={progress.total}
            aria-valuemin={1}
            aria-valuenow={progress.current}
            aria-valuetext={accessibleLabel}
            className={`UFOV-game__onboarding-progress ${className}`.trim()}
            role="progressbar"
        >
            <span aria-hidden="true" style={{ width: `${percent}%` }} />
        </div>
    );
}

export {
    OnboardingProgress,
    formatProgressLabel,
    getProgressLabelTemplate,
    parseOnboardingProgress,
};

export default OnboardingProgress;
